import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from '@/components/onboarding';
import { TermsAcceptanceContent } from '@/components/terms/TermsAcceptanceContent';
import { Button } from '@/components/ui/button';
import { useTermsAcceptance } from '@/hooks/use-terms-acceptance';
import { useToast } from '@/hooks/use-toast';
import { useMyUser } from '@/hooks/use-user';
import { api } from '@/lib/api';
import { BUSINESS_CONFIG, ROUTES } from '@/lib/constants';
import { isPersonalInfoComplete } from '@/lib/onboarding-completion';
import { useOnboardingStore } from '@/store/onboarding-store';
import type { User } from '@/types/user';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Unable to accept the Terms and Conditions. Please try again.';
}

function isSupervisorManagedCaregiver(
  user: Pick<User, 'userRole' | 'subscription'> | null | undefined
): boolean {
  return user?.userRole === 'CARE_GIVER' && user.subscription?.accessSource === 'SUPERVISOR';
}

function isAgencySupervisorManagedCaregiver(
  user: Pick<User, 'userRole' | 'supervisorContext'> | null | undefined
): boolean {
  return user?.userRole === 'CARE_GIVER' && user.supervisorContext?.supervisorType === 'AGENCY';
}

export function Terms() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRenewalMode = searchParams.get('mode') === 'renewal';
  const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false);
  const { error: showError } = useToast();
  const {
    user,
    isLoading,
    acceptCaregiverTerms: acceptTerms,
    isAcceptingCaregiverTerms: isAcceptingTerms,
  } = useMyUser();
  const isSupervisor = user?.userRole === 'SUPERVISOR';
  const { pendingProfilePhotoFile, clearPendingProfilePhotoFile, clearOnboarding } =
    useOnboardingStore();
  const {
    hasScrolledToBottom,
    hasAcceptedTerms,
    setHasAcceptedTerms,
    termsContentRef,
    handleTermsScroll,
  } = useTermsAcceptance();

  useEffect(() => {
    if (isLoading || !user) {
      return;
    }

    if (!isRenewalMode && !isPersonalInfoComplete(user)) {
      navigate(ROUTES.ONBOARDING.PERSONAL_INFO, { replace: true });
    }
  }, [isLoading, isRenewalMode, navigate, user]);

  const handleBack = () => {
    navigate(ROUTES.ONBOARDING.PERSONAL_INFO);
  };

  const uploadPendingProfilePhotoIfNeeded = async () => {
    if (!pendingProfilePhotoFile) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', pendingProfilePhotoFile);

      await api.request('/api/v1/me/care-giver-profile/photo', {
        method: 'POST',
        body: formData,
      });
    } catch {
      showError('Profile photo not uploaded', {
        description: 'You can upload your profile photo later in Profile Settings.',
      });
    } finally {
      clearPendingProfilePhotoFile();
    }
  };

  const createOrUpdateCaregiverProfile = async () => {
    await api.post('/api/v1/me/care-giver-profile', {
      bookingAvailability: 'AVAILABLE',
      profileLinkAccess: 'PUBLIC',
      bookingRequestValidation: 'REQUIRE_AVAILABILITY_SLOT',
      rolesForHire: [],
      ageRangesServed: [],
    });

    await uploadPendingProfilePhotoIfNeeded();
  };

  const handleAccept = async () => {
    const isAgencyCaregiver = isAgencySupervisorManagedCaregiver(user);
    const shouldCreateCaregiverProfile = !isSupervisor && !isAgencyCaregiver;
    setIsCompletingOnboarding(true);

    try {
      await acceptTerms();

      if (isRenewalMode) {
        navigate('/dashboard', { replace: true });
      } else {
        if (shouldCreateCaregiverProfile) {
          await createOrUpdateCaregiverProfile();
        }

        const redirectTo = isAgencyCaregiver
          ? '/dashboard/engagement-library'
          : isSupervisorManagedCaregiver(user)
            ? '/dashboard/settings?supervisedNotice=1'
            : '/dashboard/manage-plan';

        clearOnboarding();
        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      const description =
        error instanceof Error
          ? error.message
          : 'We accepted your terms, but we could not finish setting up your caregiver profile. Please try again.';

      showError('Unable to accept terms', {
        description:
          isRenewalMode || !shouldCreateCaregiverProfile
            ? getErrorMessage(error)
            : `We accepted your terms, but we could not finish setting up your caregiver profile. ${description}`,
      });
    } finally {
      setIsCompletingOnboarding(false);
    }
  };

  if (isLoading && !user) {
    return (
      <OnboardingLayout
        currentStep={2}
        totalSteps={BUSINESS_CONFIG.ONBOARDING.TOTAL_STEPS}
        title={isRenewalMode ? 'Review updated terms' : 'Accept terms and conditions'}
        subtitle="Loading the latest terms for your review."
        showBackButton={!isRenewalMode}
        showProgressIndicator={!isRenewalMode}
        onBack={handleBack}
      >
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
          <Loader2 aria-hidden="true" className="h-8 w-8 animate-spin text-blueviolet" />
          <p className="font-body text-sm text-night-500">Loading terms...</p>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      currentStep={2}
      totalSteps={BUSINESS_CONFIG.ONBOARDING.TOTAL_STEPS}
      title={isRenewalMode ? 'Review updated terms' : 'Accept terms and conditions'}
      subtitle={
        isRenewalMode
          ? 'Please review and accept the latest Terms and Conditions to continue.'
          : 'Please review and accept the Terms and Conditions before continuing.'
      }
      showBackButton={!isRenewalMode}
      showProgressIndicator={!isRenewalMode}
      onBack={handleBack}
    >
      <div className="space-y-6">
        <TermsAcceptanceContent
          checkboxId="terms-acceptance"
          hasAcceptedTerms={hasAcceptedTerms}
          hasScrolledToBottom={hasScrolledToBottom}
          isSubmitting={isAcceptingTerms}
          onAcceptedTermsChange={setHasAcceptedTerms}
          onTermsScroll={handleTermsScroll}
          scrollContainerRef={termsContentRef}
          scrollContainerTestId="onboarding-terms-scroll-container"
        />

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          {!isRenewalMode && (
            <Button type="button" variant="outline" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button
            type="button"
            onClick={handleAccept}
            disabled={!hasAcceptedTerms || isAcceptingTerms || isCompletingOnboarding}
          >
            {isAcceptingTerms || isCompletingOnboarding
              ? 'Finishing setup...'
              : isRenewalMode
                ? 'Accept Terms & Return to Dashboard'
                : 'Accept Terms & Continue'}
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
